import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import { OptimizelyProvider } from '@optimizely/react-sdk';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary, UserProfile } from 'src/components';
import { queryClient } from 'src/config/request';
import { optimizelyClient } from 'src/utils/optimizely';
import { Language } from 'src/utils/constants';
import api from 'src/services/api';
import { treatExternalLocale } from 'src/utils/functions';
import { i18nInit } from 'src/i18n';
import { ComponentName } from 'src/utils/datahubDefaultEvent';
import { IS_FIRST_ACCESS_TO_PROFILE } from 'src/components/userProfile/userProfile.constants';
import {
  EventName,
  OpenProfileEvent,
  UserProfileContextProps,
  UserProfileProviderProps
} from './userProfileContext.types';

const UserProfileContext = createContext({} as UserProfileContextProps);

export const UserProfileProvider = ({
  token,
  theme,
  loggedUserUcode,
  locale = Language.EN,
  children
}: UserProfileProviderProps) => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [userUcode, setUserUcode] = useState<string>();
  const [arrivedFrom, setArrivedFrom] = useState<ComponentName>();
  const isSessionUserProfile = !userUcode || userUcode === loggedUserUcode;

  const dispatchOpenProfileActions = (userUcode?: string) => {
    document.body.click();
    setUserUcode(userUcode);
    setVisible(true);
  };

  const onOpen = useCallback(
    (
      event?: React.MouseEvent | React.KeyboardEvent,
      userUcode?: string,
      callback?: (userUcode?: string) => void
    ) => {
      const isKeyboardEvent = typeof event === 'object' && 'key' in event;

      if (!isKeyboardEvent || (isKeyboardEvent && event.key === 'Enter')) {
        callback?.(userUcode);
        dispatchOpenProfileActions(userUcode || loggedUserUcode);
        localStorage.setItem(IS_FIRST_ACCESS_TO_PROFILE, 'true');
      }
    },
    []
  );

  const onClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => setUserUcode(undefined), 500);
  }, []);

  useLayoutEffect(() => {
    const setUserToken = () => {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    };

    const setUserLocale = () => {
      const treatedLocale = treatExternalLocale(locale);
      const userLocale = Language[treatedLocale] || Language.EN;
      i18nInit(userLocale);
    };

    setUserToken();
    setUserLocale();
  }, []);

  useEffect(() => {
    const handleOpenProfile = (event: OpenProfileEvent) => {
      onOpen(undefined, event?.detail?.ucode);
      setArrivedFrom(event?.detail?.componentName);
    };

    window.addEventListener(EventName.OPEN_PROFILE, handleOpenProfile as EventListener);
    window.addEventListener(EventName.CLOSE_PROFILE, onClose);

    return () => {
      window.removeEventListener(EventName.OPEN_PROFILE, handleOpenProfile as EventListener);
      window.removeEventListener(EventName.CLOSE_PROFILE, onClose);
    };
  }, []);

  useEffect(() => {
    onClose();
  }, [pathname]);

  return (
    <ErrorBoundary fallback={children}>
      <OptimizelyProvider optimizely={optimizelyClient} user={{ id: loggedUserUcode as string }}>
        <QueryClientProvider client={queryClient}>
          <UserProfileContext.Provider
            value={{
              visible,
              userUcode,
              loggedUserUcode,
              isSessionUserProfile,
              arrivedFrom,
              onOpen,
              onClose
            }}
          >
            {children}
            <UserProfile theme={theme} />
          </UserProfileContext.Provider>
        </QueryClientProvider>
      </OptimizelyProvider>
    </ErrorBoundary>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);

  if (!Object.keys(context).length) {
    // eslint-disable-next-line no-console
    console.log('useUserProfile must be used within a UserProfileContext');
  }

  return context;
};
